/* ==========================================================================
   Graphics kit order form (Moeed Builds, v2)
   - One kit line (the design's own variant when it has one for the chosen
     model, otherwise the shared model product) carrying every detail and
     the logo uploads as line item properties.
   - Paid upgrades and extras added as separate lines, linked by _gk_bundle.
   - If the extras can't be added, the kit line is removed again so a
     customer can never check out with an upgrade they weren't charged for.
   ========================================================================== */
(function () {
  'use strict';

  var MAX_FILES = 2;
  var MAX_BYTES = 20 * 1024 * 1024;

  function norm(s) { return String(s || '').toLowerCase().replace(/[^a-z0-9]/g, ''); }
  function $(root, sel) { return root.querySelector(sel); }
  function $$(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }

  function formatMoney(cents) {
    try {
      if (window.theme && theme.Currency && theme.settings && theme.settings.moneyFormat) {
        return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
      }
    } catch (e) { /* fall through */ }
    return '$' + (cents / 100).toFixed(2);
  }

  /* ---------- number renderer (MX number fonts, digits and letters) ---------- */
  // k: font size per pixel of number height, so every style fills the same height.
  // sp: letter spacing as a share of the number height.
  var NUM_FONTS = {
    impact:  { fam: 'GK Num Impact',  k: 1.1493, sp: -0.02 },
    slash:   { fam: 'GK Num Slash',   k: 1.4203, sp: -0.17 },
    strike:  { fam: 'GK Num Strike',  k: 1.4286, sp: -0.15 },
    factory: { fam: 'GK Num Factory', k: 1.4984, sp: -0.02 },
    rowdy:   { fam: 'GK Num Rowdy',   k: 1.4284, sp: -0.13 },
    tape:    { fam: 'GK Num Tape',    k: 1.2526, sp: -0.08 },
    stencil: { fam: 'GK Num Stencil', k: 1.0642, sp: -0.075 },
    alpha:   { fam: 'GK Num Alpha',   k: 1.2843, sp: -0.16 }
  };

  function renderNumber(el, str, font, colour, outline, plateBg) {
    var f = NUM_FONTS[font] || NUM_FONTS.impact;
    var h = parseFloat(getComputedStyle(el).height) || 96;
    var maxW = parseFloat(el.getAttribute('data-gk-max')) || ((el.parentNode && el.parentNode.clientWidth) || 200) - 12;
    el.innerHTML = '';
    var t = document.createElement('span');
    t.className = 'gk-num__t';
    t.textContent = str;
    t.style.fontFamily = '"' + f.fam + '", Impact, "Arial Narrow", sans-serif';
    t.style.color = outline ? plateBg : colour;
    if (outline) {
      t.style.filter = 'drop-shadow(3px 0 0 ' + colour + ') drop-shadow(-3px 0 0 ' + colour + ') drop-shadow(0 3px 0 ' + colour + ') drop-shadow(0 -3px 0 ' + colour + ')';
    }
    el.appendChild(t);
    var size = function (px) {
      t.style.fontSize = (px * f.k) + 'px';
      t.style.letterSpacing = (px * f.sp) + 'px';
      t.style.lineHeight = px + 'px';
    };
    size(h);
    // shrink long numbers so they always fit the plate
    var w = t.getBoundingClientRect().width;
    if (w > maxW && w > 0) size(h * maxW / w);
  }

  function GraphicsKit(root) {
    this.root = root;
    var cfgEl = $(root, '[data-gk-config]');
    this.cfg = JSON.parse(cfgEl.textContent);
    this.s = this.cfg.strings;
    this.id = root.id;
    this.modelsUrl = root.getAttribute('data-models-url') || '/search';
    this.cartAddUrl = (root.getAttribute('data-cart-add-url') || '/cart/add') + '.js';
    this.cartChangeUrl = (root.getAttribute('data-cart-change-url') || '/cart/change') + '.js';
    this.cache = {};
    this.models = [];
    this.model = null;
    this.files = [];
    this.lastFontNumber = null;

    this.el = {
      make: $(root, '[data-gk-make]'),
      model: $(root, '[data-gk-model]'),
      year: $(root, '[data-gk-year]'),
      modelPrice: $(root, '[data-gk-model-price]'),
      rname: $(root, '[data-gk-rname]'),
      rnum: $(root, '[data-gk-rnum]'),
      plateBg: $(root, '[data-gk-plate-bg]'),
      fxBase: $(root, '[data-gk-fx-base]'),
      fxLam: $(root, '[data-gk-fx-lam]'),
      fxGloss: $(root, '[data-gk-fx-gloss]'),
      plateNum: $(root, '[data-gk-plate-num]'),
      plateName: $(root, '[data-gk-plate-name]'),
      plateCaption: $(root, '[data-gk-plate-caption]'),
      sameWarn: $(root, '[data-gk-same-warn]'),
      drop: $(root, '[data-gk-drop]'),
      file: $(root, '[data-gk-file]'),
      files: $(root, '[data-gk-files]'),
      fileErr: $(root, '[data-gk-file-err]'),
      lines: $(root, '[data-gk-lines]'),
      total: $(root, '[data-gk-total]'),
      atc: $(root, '[data-gk-atc]'),
      atcText: $(root, '[data-gk-atc-text]'),
      atcTotal: $(root, '[data-gk-atc-total]'),
      formErr: $(root, '[data-gk-form-err]')
    };

    var section = root.closest('[data-section-id]') || document;
    this.priceEl = section.querySelector('[data-product-price]');

    this.bind();
    this.update();
  }

  GraphicsKit.prototype = {
    /* ---------- events ---------- */
    bind: function () {
      var self = this, el = this.el, root = this.root;

      // Never let the theme (or the old options app) submit the product form for us.
      var form = root.closest('form');
      if (form) {
        form.addEventListener('submit', function (e) {
          e.preventDefault();
          e.stopImmediatePropagation();
        }, true);
      }
      // Enter moves to the next field instead of submitting.
      root.addEventListener('keydown', function (e) {
        if (e.key !== 'Enter') return;
        var t = e.target;
        if (t.tagName === 'TEXTAREA' || t === el.atc || t === el.drop) return;
        if (t.tagName === 'INPUT' || t.tagName === 'SELECT') {
          e.preventDefault();
          e.stopPropagation();
          var fields = $$(root, 'input.gk-input, select.gk-select, textarea.gk-input').filter(function (f) { return !f.disabled; });
          var i = fields.indexOf(t);
          if (i > -1 && fields[i + 1]) fields[i + 1].focus();
        }
      });

      el.make.addEventListener('change', function () { self.onMake(); });
      el.model.addEventListener('change', function () {
        var i = el.model.value;
        self.model = i === '' ? null : self.models[parseInt(i, 10)];
        self.clearError('model');
        self.update();
      });
      el.year.addEventListener('change', function () { self.clearError('year'); self.update(); });

      el.rnum.addEventListener('input', function () {
        var v = el.rnum.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 4);
        if (v !== el.rnum.value) el.rnum.value = v;
      });
      root.addEventListener('input', function () { self.update(); });
      root.addEventListener('change', function (e) {
        if (e.target.matches('[data-gk-addon]')) self.syncAddons();
        if (e.target.matches('[data-gk-choice]')) self.onChoice(e.target);
        self.update();
      });

      // add-on focus ring (checkbox is visually hidden)
      $$(root, '[data-gk-addon]').forEach(function (cb) {
        cb.addEventListener('focus', function () { cb.closest('.gk-addon').classList.add('is-focus'); });
        cb.addEventListener('blur', function () { cb.closest('.gk-addon').classList.remove('is-focus'); });
      });

      // uploads
      el.drop.addEventListener('click', function () { el.file.click(); });
      el.drop.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); el.file.click(); }
      });
      ['dragenter', 'dragover'].forEach(function (ev) {
        el.drop.addEventListener(ev, function (e) { e.preventDefault(); el.drop.classList.add('is-over'); });
      });
      ['dragleave', 'drop'].forEach(function (ev) {
        el.drop.addEventListener(ev, function (e) { e.preventDefault(); el.drop.classList.remove('is-over'); });
      });
      el.drop.addEventListener('drop', function (e) { self.addFiles(e.dataTransfer.files); });
      el.file.addEventListener('change', function () { self.addFiles(el.file.files); el.file.value = ''; });

      el.atc.addEventListener('click', function () { self.addToCart(); });

      window.addEventListener('resize', function () { self.lastFontNumber = null; self.update(); });

      // redraw once the number fonts arrive, so long numbers are measured in the real font
      if (document.fonts && document.fonts.load) {
        var loads = Object.keys(NUM_FONTS).map(function (k) {
          return document.fonts.load('40px "' + NUM_FONTS[k].fam + '"', '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ');
        });
        Promise.all(loads).then(function () { self.lastFontNumber = null; self.update(); }, function () {});
      }
    },

    /* ---------- step 1 ---------- */
    onMake: function () {
      var self = this, el = this.el;
      var opt = el.make.options[el.make.selectedIndex];
      var handle = opt ? opt.getAttribute('data-handle') : '';
      this.model = null;
      this.models = [];
      this.clearError('make');
      this.setModelOptions([], this.s.chooseMakeFirst, true);
      this.update();
      if (!el.make.value || !handle) return;

      this.setModelOptions([], this.s.loadingModels, true);
      var token = this.makeToken = Date.now();
      this.loadModels(handle).then(function (models) {
        if (token !== self.makeToken) return;
        self.models = models;
        if (!models.length) { self.setModelOptions([], self.s.noModels, true); return; }
        self.setModelOptions(models, self.s.chooseModel, false);
      }).catch(function () {
        if (token !== self.makeToken) return;
        self.setModelOptions([], self.s.modelsError, true);
      }).then(function () { self.update(); });
    },

    loadModels: function (handle) {
      if (this.cache[handle]) return Promise.resolve(this.cache[handle]);
      var self = this;
      var url = this.modelsUrl + '?q=' + encodeURIComponent(handle) + '&view=gk-models';
      return fetch(url, { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error('models'); return r.text(); })
        .then(function (text) {
          var start = text.indexOf('['), end = text.lastIndexOf(']');
          if (start < 0 || end < start) throw new Error('models');
          var list = JSON.parse(text.slice(start, end + 1)).filter(function (m) { return m && m.id; });
          self.cache[handle] = list;
          return list;
        });
    },

    setModelOptions: function (models, placeholder, disabled) {
      var sel = this.el.model;
      sel.innerHTML = '';
      sel.appendChild(new Option(placeholder, ''));
      models.forEach(function (m, i) { sel.appendChild(new Option(m.name, String(i))); });
      sel.disabled = !!disabled;
    },

    /* The kit line: the design's own variant if it has one for this model. */
    resolveKitLine: function () {
      if (!this.model) return null;
      var target = norm(this.model.name);
      var match = null;
      this.cfg.product.variants.forEach(function (v) {
        if (!match && v.available && norm(v.title) === target) match = v;
      });
      if (match) return { id: match.id, price: match.price, fallback: false };
      return { id: this.model.id, price: this.model.price, fallback: true };
    },

    /* ---------- selections ---------- */
    checked: function (sel) { return $(this.root, sel + ':checked'); },

    materials: function () {
      return $$(this.root, '[data-gk-mat-group]').map(function (g) {
        return { name: g.getAttribute('data-gk-group-name'), input: g.querySelector('input:checked') };
      });
    },

    paidExtras: function () {
      var out = [];
      // kp / kv / kd tell the cart which kit detail this extra fills in, so removing
      // the extra on its own also corrects the kit's details (see graphics-kit-cart.js).
      var root = this.root;
      this.materials().forEach(function (m) {
        if (m.input && parseInt(m.input.dataset.price, 10) > 0) {
          var group = $(root, '[data-gk-mat-group][data-gk-group-name="' + m.name.replace(/"/g, '\\"') + '"]');
          var free = group && $$(group, 'input').filter(function (i) { return !(parseInt(i.dataset.price, 10) > 0); })[0];
          out.push({ id: m.input.dataset.variantId, price: parseInt(m.input.dataset.price, 10), label: m.input.dataset.short + ' (' + m.name.toLowerCase() + ')',
            kp: m.name, kv: m.input.dataset.title, kd: free ? free.dataset.title : '' });
        }
      });
      $$(this.root, '[data-gk-pack]:checked').forEach(function (pack) {
        if (pack.dataset.variantId) out.push({ id: pack.dataset.variantId, price: parseInt(pack.dataset.price, 10), label: 'Mini plates, ' + pack.dataset.short,
          kp: 'Mini Plates', kv: pack.dataset.title });
      });
      $$(this.root, '[data-gk-addon]:checked').forEach(function (a) {
        var label = a.dataset.title.replace(/^add\s+(an?\s+)?/i, '');
        label = label.charAt(0).toUpperCase() + label.slice(1);
        if (a.dataset.choice) label += ', ' + a.dataset.choice;
        out.push({ id: a.dataset.variantId, price: parseInt(a.dataset.price, 10), label: label });
      });
      return out;
    },

    syncAddons: function () {
      $$(this.root, '[data-gk-addon]').forEach(function (cb) {
        cb.closest('.gk-addon').classList.toggle('is-checked', cb.checked);
        var choices = cb.closest('.gk-addon-wrap') && cb.closest('.gk-addon-wrap').querySelector('[data-gk-choices]');
        if (choices) {
          choices.hidden = !cb.checked;
          if (!cb.checked) choices.classList.remove('has-error');
        }
      });
    },

    onChoice: function (radio) {
      var wrap = radio.closest('.gk-addon-wrap');
      var cb = wrap.querySelector('[data-gk-addon]');
      cb.dataset.variantId = radio.dataset.variantId;
      cb.dataset.price = radio.dataset.price;
      cb.dataset.choice = radio.dataset.title;
      wrap.querySelector('[data-gk-choices]').classList.remove('has-error');
    },

    /* ---------- uploads ---------- */
    addFiles: function (list) {
      var self = this, errs = [];
      Array.prototype.forEach.call(list || [], function (f) {
        if (self.files.length >= MAX_FILES) { errs.push(self.s.maxFiles); return; }
        if (f.size > MAX_BYTES) { errs.push(f.name + ' ' + self.s.fileTooBig); return; }
        self.files.push({ file: f, url: /^image\//.test(f.type) && f.type !== 'image/svg+xml' ? URL.createObjectURL(f) : '' });
      });
      this.el.fileErr.textContent = errs.filter(function (e, i, a) { return a.indexOf(e) === i; }).join(' ');
      this.drawFiles();
      this.update();
    },

    drawFiles: function () {
      var self = this, ul = this.el.files;
      ul.innerHTML = '';
      this.files.forEach(function (o, i) {
        var li = document.createElement('li');
        var thumb = document.createElement('span');
        thumb.className = 'gk-files__thumb';
        if (o.url) thumb.style.backgroundImage = 'url(' + o.url + ')';
        else thumb.textContent = (o.file.name.split('.').pop() || '').toUpperCase().slice(0, 4);
        var name = document.createElement('span');
        name.className = 'gk-files__name';
        name.textContent = o.file.name;
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.textContent = 'Remove';
        btn.setAttribute('aria-label', 'Remove ' + o.file.name);
        btn.addEventListener('click', function () {
          if (o.url) URL.revokeObjectURL(o.url);
          self.files.splice(i, 1);
          self.el.fileErr.textContent = '';
          self.drawFiles();
          self.update();
          self.el.drop.focus();
        });
        li.appendChild(thumb); li.appendChild(name); li.appendChild(btn);
        ul.appendChild(li);
      });
      this.el.drop.hidden = this.files.length >= MAX_FILES;
    },

    /* ---------- render ---------- */
    update: function () {
      var self = this, el = this.el, root = this.root, s = this.s;
      var kit = this.resolveKitLine();

      // product price at the top of the page
      if (this.priceEl) {
        if (kit) {
          this.priceEl.textContent = formatMoney(kit.price) + ' ' + s.forModel + ' ' + this.model.name;
        } else {
          var p = this.cfg.product;
          this.priceEl.textContent = (p.priceVaries ? s.from + ' ' : '') + formatMoney(p.priceMin);
        }
      }
      if (kit) {
        el.modelPrice.hidden = false;
        el.modelPrice.innerHTML = '';
        el.modelPrice.appendChild(document.createTextNode('Kit price for the ' + this.model.name + ': '));
        var b = document.createElement('b'); b.textContent = formatMoney(kit.price);
        el.modelPrice.appendChild(b);
      } else {
        el.modelPrice.hidden = true;
      }

      // number plate
      var font = (this.checked('[data-gk-font]') || {}).dataset;
      font = font ? font.gkFont : 'asis';
      var ncIn = this.checked('[data-gk-nc]'), bcIn = this.checked('[data-gk-bc]');
      var nc = ncIn ? ncIn.dataset.gkNc : 'asis', bc = bcIn ? bcIn.dataset.gkBc : 'asis';
      var outline = !!(ncIn && ncIn.hasAttribute('data-gk-outline'));
      var bgHex = bc === 'asis' ? '#ffffff' : bc;
      var ncHex = nc === 'asis' ? '#0a0a0a' : nc;
      var num = el.rnum.value.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
      el.plateBg.setAttribute('fill', bgHex);
      el.plateNum.classList.toggle('is-empty', !num);
      renderNumber(el.plateNum, num || '23', font === 'asis' ? 'impact' : font, ncHex, outline, bgHex);
      el.plateName.textContent = el.rname.value.trim();
      var nameFont = ((this.checked('[data-gk-font]') || {}).dataset || {}).gkNameFont || 'impact';
      var NAME_FONTS = { race: 'GK Name Race', strike: 'GK Name Strike', rowdy: 'GK Name Rowdy', tape: 'GK Name Tape', stencil: 'GK Name Stencil', alpha: 'GK Name Alpha' };
      el.plateName.style.fontFamily = (NAME_FONTS[nameFont] ? '"' + NAME_FONTS[nameFont] + '", ' : '') + 'Impact, "GK Name Impact", "Arial Narrow", sans-serif';
      el.plateName.style.color = ncHex;
      el.sameWarn.classList.toggle('is-visible', nc !== 'asis' && bc !== 'asis' && !outline && nc.toLowerCase() === bc.toLowerCase());

      // chosen labels next to the font and colour headings
      var chosen = function (k, input) {
        var t = $(root, '[data-gk-chosen="' + k + '"]');
        if (!t) return;
        var v = input ? input.value : '';
        t.textContent = !v || /^same as shown/i.test(v) ? 'Same as shown on website sample' : v.replace(' - ', ', ');
      };
      chosen('font', this.checked('[data-gk-font]'));
      chosen('nc', ncIn);
      chosen('bc', bcIn);

      // plate materials
      var mats = this.materials();
      var baseFx = '', lamFx = '', capParts = [];
      mats.forEach(function (m) {
        if (!m.input) return;
        var fx = m.input.dataset.fx || '';
        if (fx.indexOf('m-') === 0 && !baseFx) baseFx = fx;
        if (fx.indexOf('l-') === 0 && !lamFx) lamFx = fx;
        capParts.push(m.input.dataset.short.toLowerCase());
      });
      // The plate always shows standard gloss: real chrome and holographic finishes
      // can't be shown accurately on screen, so they appear on the free proof instead.
      el.fxBase.setAttribute('fill', 'none');
      el.fxLam.setAttribute('fill', 'none');
      el.fxGloss.style.opacity = '1';

      // font tiles show the rider's own number
      var tileNum = num || '23';
      if (tileNum !== this.lastFontNumber) {
        this.lastFontNumber = tileNum;
        $$(root, '[data-gk-font-sample]').forEach(function (n) {
          renderNumber(n, tileNum, n.getAttribute('data-gk-font-sample'), '#ffffff', false, '#000');
        });
      }

      // step states and one-line summaries
      var fontName = font === 'asis' ? '' : $(root, '[data-gk-font="' + font + '"]').value + ' font';
      var stepDone = {
        bike: !!(el.make.value && this.model && el.year.value),
        rider: !!(el.rname.value.trim() || num || font !== 'asis' || nc !== 'asis' || bc !== 'asis'),
        logos: false,
        materials: true,
        extras: false
      };
      var logoCount = $$(root, '[data-gk-logo]').filter(function (i) { return i.value.trim(); }).length;
      var changes = $(root, '[data-gk-changes]').value.trim();
      stepDone.logos = !!(logoCount || changes || this.files.length);
      var extras = this.paidExtras().filter(function (x) { return !/\(/.test(x.label); });
      stepDone.extras = extras.length > 0;

      var sums = {
        bike: [el.make.value, this.model ? this.model.name : '', el.year.value ? ', ' + el.year.value : ''].join(' ').replace(/\s+,/, ',').trim(),
        rider: [el.rname.value.trim(), num ? '#' + num : '', fontName].filter(Boolean).join(', ') || 'Colors updated',
        logos: [logoCount ? logoCount + (logoCount > 1 ? ' logo changes' : ' logo change') : '', this.files.length ? this.files.length + (this.files.length > 1 ? ' files' : ' file') + ' uploaded' : '', changes ? 'design notes added' : ''].filter(Boolean).join(', '),
        materials: capParts.map(function (c, i) { return i === 0 ? c.charAt(0).toUpperCase() + c.slice(1) : c; }).join(', '),
        extras: extras.map(function (x) { return x.label; }).join(', ')
      };
      $$(root, '[data-gk-step]').forEach(function (step) {
        var k = step.getAttribute('data-gk-step');
        step.classList.toggle('is-done', !!stepDone[k]);
        var sum = step.querySelector('[data-gk-sum]');
        if (sum) sum.textContent = stepDone[k] ? sums[k] : '';
      });
      if (stepDone.bike) el.formErr.textContent = '';

      // summary lines + totals
      var lines = [];
      lines.push(kit
        ? { cls: 'is-kit', label: this.cfg.product.title + ', ' + this.model.name, price: kit.price }
        : { cls: 'is-placeholder', label: 'Choose your bike in step 1', price: null });
      this.paidExtras().forEach(function (x) { lines.push({ label: x.label, price: x.price }); });
      el.lines.innerHTML = '';
      var total = 0;
      lines.forEach(function (l) {
        var li = document.createElement('li');
        if (l.cls) li.className = l.cls;
        var a = document.createElement('span'); a.textContent = l.label;
        var b2 = document.createElement('span'); b2.textContent = l.price === null ? '' : formatMoney(l.price);
        li.appendChild(a); li.appendChild(b2);
        el.lines.appendChild(li);
        total += l.price || 0;
      });
      el.total.textContent = formatMoney(total);
      el.atcTotal.textContent = formatMoney(total);
    },

    /* ---------- validation ---------- */
    clearError: function (k) {
      var f = $(this.root, '[data-gk-field="' + k + '"]');
      if (f) f.classList.remove('has-error');
    },

    validate: function () {
      var el = this.el, bad = null, root = this.root;
      [['make', el.make.value], ['model', this.model], ['year', el.year.value]].forEach(function (pair) {
        if (!pair[1]) {
          $(root, '[data-gk-field="' + pair[0] + '"]').classList.add('has-error');
          bad = bad || pair[0];
        }
      });
      if (!bad) {
        var missingChoice = $$(root, '[data-gk-addon][data-gk-has-choices]:checked').filter(function (cb) { return !cb.dataset.variantId; })[0];
        if (missingChoice) {
          var box = missingChoice.closest('.gk-addon-wrap').querySelector('[data-gk-choices]');
          box.classList.add('has-error');
          el.formErr.textContent = 'Pick a design for ' + missingChoice.dataset.title.replace(/^add\s+(an?\s+)?/i, '').toLowerCase() + ' in step 5, or untick it.';
          box.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return false;
        }
      }
      if (bad) {
        el.formErr.textContent = this.s.missingBike;
        var target = el[bad];
        $(root, '[data-gk-step="bike"]').scrollIntoView({ behavior: 'smooth', block: 'start' });
        setTimeout(function () { (target.disabled ? el.make : target).focus({ preventScroll: true }); }, 450);
        return false;
      }
      return true;
    },

    /* ---------- add to cart ---------- */
    collectProperties: function (bundle, kit) {
      var el = this.el, root = this.root, props = [];
      function add(k, v) { if (v !== undefined && v !== null && String(v).trim() !== '') props.push([k, String(v).trim()]); }

      if (kit.fallback) add('Design', this.cfg.product.title);
      add('Make', el.make.value);
      add('Model', this.model.name);
      add('Year', el.year.value);
      add('Rider Name', el.rname.value);
      add('Rider Number', el.rnum.value);
      add('Number Font', (this.checked('[data-gk-font]') || {}).value);
      add('Number Color', (this.checked('[data-gk-nc]') || {}).value);
      add('Background Color', (this.checked('[data-gk-bc]') || {}).value);
      $$(root, '[data-gk-logo]').forEach(function (i) { add(i.getAttribute('data-gk-prop'), i.value); });
      add('Design Changes', $(root, '[data-gk-changes]').value);
      this.materials().forEach(function (m) { if (m.input) add(m.name, m.input.dataset.title); });
      var packs = $$(root, '[data-gk-pack]:checked').map(function (p) { return p.dataset.title; });
      if ($(root, '[data-gk-pack]')) add('Mini Plates', packs.length ? packs.join(', ') + ' (plus 3 free)' : 'Each order includes 3 free mini plates');
      add('Additional Notes', $(root, '[data-gk-prop="Additional Notes"]').value);
      add('_gk_bundle', bundle);
      add('_gk_role', 'kit');
      if (kit.fallback && this.cfg.product.image) add('_gk_image', this.cfg.product.image);
      return props;
    },

    setBusy: function (busy) {
      var el = this.el;
      el.atc.setAttribute('aria-busy', busy ? 'true' : 'false');
      el.atc.disabled = !!busy;
      el.atcText.textContent = busy ? this.s.adding + '…' : 'Add to cart';
    },

    postJSON: function (url, body) {
      return fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body)
      }).then(function (r) {
        return r.json().catch(function () { return {}; }).then(function (data) {
          if (!r.ok || data.status) { var e = new Error(data.description || data.message || 'cart'); e.data = data; throw e; }
          return data;
        });
      });
    },

    addToCart: function () {
      var self = this, el = this.el;
      if (el.atc.getAttribute('aria-busy') === 'true') return;
      el.formErr.textContent = '';
      if (!this.validate()) return;

      var kit = this.resolveKitLine();
      var bundle = 'gk' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
      var forKit = this.cfg.product.title + ' (' + this.model.name + ')';

      var fd = new FormData();
      fd.append('id', kit.id);
      fd.append('quantity', '1');
      this.collectProperties(bundle, kit).forEach(function (p) { fd.append('properties[' + p[0] + ']', p[1]); });
      this.files.forEach(function (o, i) { fd.append('properties[Logo ' + (i + 1) + ']', o.file, o.file.name); });

      var extras = this.paidExtras().map(function (x) {
        var props = { 'For kit': forKit, '_gk_bundle': bundle, '_gk_role': 'addon' };
        // One hidden note: which kit detail this extra fills, and the free option to fall back to.
        if (x.kp) props._gk_fills = x.kd ? x.kp + '|' + x.kd : x.kp;
        return { id: parseInt(x.id, 10), quantity: 1, properties: props };
      });

      this.setBusy(true);
      var extraKeys = [];
      var cartUpdateUrl = this.cartChangeUrl.replace(/change\.js$/, 'update.js');

      // Extras go in first and the kit last, so the kit sits at the top of the cart.
      var addExtras = extras.length
        ? self.postJSON(self.cartAddUrl, { items: extras }).then(function (res) {
            extraKeys = ((res && res.items) || []).map(function (it) { return it.key; }).filter(Boolean);
          }).catch(function () {
            // Clear out anything from this kit that did make it in, then explain.
            var cartJs = (window.theme && theme.routes && theme.routes.cart) || '/cart.js';
            return fetch(cartJs, { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
              .then(function (r) { return r.json(); })
              .then(function (cart) {
                var updates = {}, any = false;
                (cart.items || []).forEach(function (it) {
                  if (it.properties && it.properties._gk_bundle === bundle) { updates[it.key] = 0; any = true; }
                });
                return any ? self.postJSON(cartUpdateUrl, { updates: updates }) : null;
              })
              .catch(function () {})
              .then(function () { var e = new Error(self.s.extrasError); e.friendly = true; throw e; });
          })
        : Promise.resolve();

      addExtras.then(function () {
        return fetch(self.cartAddUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: fd
        }).then(function (r) {
          return r.json().catch(function () { return {}; }).then(function (data) {
            if (!r.ok || data.status) { var e = new Error(data.description || data.message || 'kit'); e.data = data; throw e; }
            return data;
          });
        }).catch(function (err) {
          // The kit failed: take the extras back out so they're never left in the cart on their own.
          if (!extraKeys.length) throw err;
          var updates = {};
          extraKeys.forEach(function (k) { updates[k] = 0; });
          return self.postJSON(cartUpdateUrl, { updates: updates }).catch(function () {}).then(function () { throw err; });
        });
      }).then(function (data) {
        self.setBusy(false);
        if (window.theme && theme.settings && theme.settings.cartType === 'page' && theme.routes && theme.routes.cartPage) {
          window.location = theme.routes.cartPage;
          return;
        }
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: data, addToCartBtn: el.atc } }));
      }).catch(function (err) {
        self.setBusy(false);
        var msg = err && err.friendly ? err.message : (err && err.data && err.data.description) ? err.data.description : self.s.addError;
        el.formErr.textContent = msg;
      });
    }
  };

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-gk]'), function (root) {
      if (root.__gk) return;
      try { root.__gk = new GraphicsKit(root); } catch (e) { if (window.console) console.error('Graphics kit form failed to start', e); }
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();